import { REMINDER_TYPE_CUSTOM } from "@/constants";
import type { EventFormData } from "@/features/study-group-events/type";
import type { EventCreateApiRequest } from "@/lib/humhub-api/types";
import { dedupeReminderItems } from "@/lib/utils/event/form-initializer";

/**
 * NOTE: 型はこのファイル（APIシリアライザの境界）内に局所化して狭める。
 *
 * 背景/方針:
 * - 画面側（useState<EventFormData>）は入力途中の不完全値（時刻未入力など）を許容したい。
 * - `EventFormData` をアプリ全体で判別共用体にすると、部分更新（例: {...form, location_type}）に型エラーが波及する。
 * - そのため、送信直前のこの層でのみ厳密化（終日/時刻）を行うのが責務分離として適切。
 * - 専用型の export に依存しないため、型解決の環境差（型が見えない等）にも強い。
 *
 * 実装要点:
 * - 終日(all_day=1): HumHubの終端排他仕様に合わせ、UIの `endDate` から +1日した日付を `end_date` として送る。
 * - 時刻指定(all_day=0): フォーム上の日時をそのまま `start_date/start_time/end_date/end_time` として送る。
 *   `timeZone` とセットでサーバー側に解釈させる。
 * - 不変条件: isAllDay=false の場合、startTime/endTime は必須。未入力はここでエラーとして送信を止める。
 */

// Local helper types to narrow without exporting global unions
// 終日: フォーム値から startTime/endTime を取り除いたシェイプを明示
type AllDayForm = { isAllDay: true } & Omit<
  EventFormData,
  "startTime" | "endTime"
>;
type TimedForm = {
  isAllDay: false;
  startTime: string;
  endTime: string;
} & EventFormData;

const isAllDayForm = (data: EventFormData): data is AllDayForm =>
  data.isAllDay === true;
const isTimedForm = (data: EventFormData): data is TimedForm =>
  data.isAllDay === false &&
  typeof data.startTime === "string" &&
  typeof data.endTime === "string";

// 終日用に startTime/endTime を落としたオブジェクトへ整形
const removeTimeFieldsFromAllDayForm = (data: EventFormData): AllDayForm => {
  const { startTime: _st, endTime: _et, ...rest } = data;
  return rest as AllDayForm;
};

const getCurrentBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn("Failed to get browser timezone, falling back to UTC:", error);
    return "UTC";
  }
};

// 共有: CalendarEntryForm の共通フィールド（上書きは呼び出し側で formData を作って渡す）
const buildEventFormCommonFields = (
  formData: EventFormData,
  timeZone: string,
  options?: { alwaysIncludeRecurrence?: boolean; omitScope?: boolean },
) => {
  const shouldSendRecurrence =
    options?.alwaysIncludeRecurrence || formData.scope !== "single";

  return {
    timeZone,
    location: formData.location,
    language: formData.language,
    is_draft: formData.is_draft,
    forceJoin: 0,
    topics: formData.channel || "",
    is_public: formData.is_public,
    reminders: dedupeReminderItems(formData.reminders).map((r) => ({
      type: REMINDER_TYPE_CUSTOM,
      unit: r.unit,
      value: r.value,
    })),
    hidden: formData.hidden,
    online_location: formData.online_location,
    location_type: formData.location_type,
    // scope が single（このインスタンスのみ）の場合は recurrence を送らず、新規の繰り返し生成を防ぐ
    recurrence: shouldSendRecurrence
      ? (formData.recurrence ?? undefined)
      : undefined,
    scope: options?.omitScope ? undefined : (formData.scope ?? "single"),
  };
};

// 作成: フォーム → API リクエスト
export const transformFormToCreateApiRequest = (
  formData: EventFormData,
): EventCreateApiRequest => {
  return buildRequest(formData, {
    alwaysIncludeRecurrence: true,
    omitScope: true,
  });
};

// 更新: フォーム → API リクエスト（フォーム指定TZ優先、未指定時は既存TZ）
export const transformFormToUpdateApiRequest = (
  formData: EventFormData,
  existingTimeZone: string,
): EventCreateApiRequest => {
  return buildRequest(formData, {
    timeZone: formData.timeZone ?? existingTimeZone,
  });
};

// ドラフト保存用
export const transformFormToDraftApiRequest = (
  formData: EventFormData,
): EventCreateApiRequest => {
  const draftForm = { ...formData, is_draft: 1, hidden: 1 };
  return transformFormToCreateApiRequest(draftForm);
};

// 共通ビルダー
function buildRequest(
  formData: EventFormData,
  options?: {
    timeZone?: string;
    alwaysIncludeRecurrence?: boolean;
    omitScope?: boolean;
  },
): EventCreateApiRequest {
  const timeZone =
    formData.timeZone ?? options?.timeZone ?? getCurrentBrowserTimeZone();

  const baseCalendarEntry = {
    title: formData.title,
    description: formData.description,
    color: "",
    participation_mode: 2,
    max_participants: 0,
    allow_decline: 0,
    allow_maybe: 0,
    participant_info: "",
  };

  // 終日イベント
  if (isAllDayForm(formData)) {
    const allDayEvent = removeTimeFieldsFromAllDayForm(formData);

    return {
      CalendarEntry: {
        ...baseCalendarEntry,
        all_day: 1,
      },
      CalendarEntryForm: {
        start_date: allDayEvent.startDate,
        end_date: allDayEvent.endDate,
        ...buildEventFormCommonFields(allDayEvent, timeZone, {
          alwaysIncludeRecurrence: options?.alwaysIncludeRecurrence,
          omitScope: options?.omitScope,
        }),
      },
    };
  }

  // 時刻指定イベント
  // 不変条件: isAllDay=false の場合、startTime/endTime は必須
  if (!isTimedForm(formData)) {
    throw new Error(
      "Timed event requires startTime and endTime. UI validation should prevent this state.",
    );
  }

  return {
    CalendarEntry: {
      ...baseCalendarEntry,
      all_day: 0,
    },
    CalendarEntryForm: {
      start_date: formData.startDate,
      start_time: formData.startTime,
      end_date: formData.endDate,
      end_time: formData.endTime,
      ...buildEventFormCommonFields(formData, timeZone, {
        alwaysIncludeRecurrence: options?.alwaysIncludeRecurrence,
        omitScope: options?.omitScope,
      }),
    },
  };
}
